import { useState, useEffect } from 'react';
import SubHeader from '@/pages/profile/components/SubHeader';
import BottomSheet from '@/pages/profile/components/BottomSheet';
import { useToast } from '@/components/base/Toast';
import { supabase } from '@/lib/supabase';

interface Address {
  id: number;
  name: string;
  phone: string;
  region: string;
  detail: string;
  isDefault: boolean;
}

const emptyForm = { name: '', phone: '', region: '', detail: '' };

export default function AddressPage() {
  const { showToast } = useToast();
  const [list, setList] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;

      if (!userId) {
        setList([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('addresses')
        .select('*')
        .eq('user_id', userId)
        .order('is_default', { ascending: false });

      if (error) throw error;

      setList(
        (data || []).map((row) => ({
          id: row.id,
          name: row.name || '',
          phone: row.phone || '',
          region: row.region || '',
          detail: row.detail || '',
          isDefault: row.is_default ?? false,
        })),
      );
    } catch {
      showToast('地址加载失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (addr: Address) => {
    setEditingId(addr.id);
    setForm({ name: addr.name, phone: addr.phone, region: addr.region, detail: addr.detail });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.phone.trim() || !form.region.trim() || !form.detail.trim()) {
      showToast('请完整填写收货信息', 'error');
      return;
    }

    if (editingId === null) {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;

      const { error } = await supabase
        .from('addresses')
        .insert({
          user_id: userId,
          name: form.name,
          phone: form.phone,
          region: form.region,
          detail: form.detail,
          is_default: list.length === 0,
        });

      if (error) {
        showToast('地址添加失败', 'error');
        return;
      }
      showToast('地址添加成功');
    } else {
      const { error } = await supabase
        .from('addresses')
        .update({
          name: form.name,
          phone: form.phone,
          region: form.region,
          detail: form.detail,
        })
        .eq('id', editingId);

      if (error) {
        showToast('地址更新失败', 'error');
        return;
      }
      showToast('地址已更新');
    }
    setShowForm(false);
    fetchData();
  };

  const handleDelete = async () => {
    if (deletingId === null) return;
    const { error } = await supabase.from('addresses').delete().eq('id', deletingId);
    if (error) {
      showToast('地址删除失败', 'error');
    } else {
      showToast('地址已删除');
    }
    setDeletingId(null);
    fetchData();
  };

  const setDefault = async (id: number) => {
    await supabase.from('addresses').update({ is_default: false }).neq('id', id);
    await supabase.from('addresses').update({ is_default: true }).eq('id', id);
    showToast('已设为默认地址');
    fetchData();
  };

  return (
    <div className="min-h-screen bg-brown-50 pb-44">
      <SubHeader title="收货地址" />

      <div className="px-4 mt-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin"></span>
          </div>
        ) : list.length === 0 ? (
          <div className="text-center py-16 text-brown-400 text-sm">暂无收货地址</div>
        ) : (
          list.map((addr) => (
            <div key={addr.id} className="bg-white rounded-2xl p-4 shadow-sm animate-fade-up">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-brown-900 text-sm font-semibold">{addr.name}</span>
                    <span className="text-brown-500 text-xs">{addr.phone}</span>
                    {addr.isDefault && (
                      <span className="bg-gold-100 text-gold-700 text-[10px] px-2 py-0.5 rounded-full">
                        默认
                      </span>
                    )}
                  </div>
                  <p className="text-brown-600 text-sm leading-relaxed">
                    {addr.region} {addr.detail}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-brown-50">
                <button
                  onClick={() => setDefault(addr.id)}
                  className={`flex items-center gap-1 text-xs cursor-pointer whitespace-nowrap ${
                    addr.isDefault ? 'text-gold-600' : 'text-brown-400'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={addr.isDefault ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'}></i>
                  </span>
                  {addr.isDefault ? '默认地址' : '设为默认'}
                </button>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => openEdit(addr)}
                    className="flex items-center gap-1 text-brown-500 text-xs cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-edit-line"></i>
                    </span>
                    编辑
                  </button>
                  <button
                    onClick={() => setDeletingId(addr.id)}
                    className="flex items-center gap-1 text-brown-500 text-xs cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-delete-bin-line"></i>
                    </span>
                    删除
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Button */}
      <div className="fixed bottom-0 left-0 right-0 px-4 pb-5 pt-4 z-30 bg-gradient-to-t from-brown-50 to-transparent">
        <button
          onClick={openAdd}
          className="w-full py-3 rounded-full bg-gold-500 hover:bg-gold-600 text-brown-950 text-sm font-semibold cursor-pointer whitespace-nowrap shadow-sm active:scale-[0.98] transition-transform"
        >
          新增收货地址
        </button>
      </div>

      {/* Form Sheet */}
      <BottomSheet open={showForm} onClose={() => setShowForm(false)} title={editingId === null ? '新增地址' : '编辑地址'}>
        <div className="px-4 pb-8 space-y-4">
          {[
            { key: 'name', label: '收货人', placeholder: '请输入收货人姓名' },
            { key: 'phone', label: '手机号', placeholder: '请输入手机号' },
            { key: 'region', label: '所在地区', placeholder: '省 / 市 / 区' },
          ].map((field) => (
            <div key={field.key}>
              <label className="block text-brown-600 text-sm mb-1.5">{field.label}</label>
              <input
                type="text"
                value={form[field.key as keyof typeof form]}
                onChange={(e) => setForm({ ...form, [field.key]: e.target.value })}
                placeholder={field.placeholder}
                className="w-full text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-xl px-4 py-3 bg-white outline-none focus:border-gold-500 transition-colors"
              />
            </div>
          ))}
          <div>
            <label className="block text-brown-600 text-sm mb-1.5">详细地址</label>
            <textarea
              value={form.detail}
              onChange={(e) => setForm({ ...form, detail: e.target.value })}
              placeholder="街道、门牌号、小区等详细信息"
              rows={3}
              maxLength={200}
              className="w-full text-sm text-brown-900 placeholder:text-brown-300 border border-brown-200 rounded-xl px-4 py-3 bg-white outline-none focus:border-gold-500 transition-colors resize-none"
            />
          </div>
          <button
            onClick={handleSave}
            className="w-full py-3.5 rounded-full bg-gold-500 text-brown-950 text-sm font-semibold cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform"
          >
            保存地址
          </button>
        </div>
      </BottomSheet>

      {/* Delete Confirm Sheet */}
      <BottomSheet open={deletingId !== null} onClose={() => setDeletingId(null)} title="删除地址">
        <div className="px-4 pb-8">
          <p className="text-brown-600 text-sm text-center py-4">确定要删除该收货地址吗？</p>
          <div className="flex gap-3">
            <button
              onClick={() => setDeletingId(null)}
              className="flex-1 py-3 rounded-full border border-brown-200 text-brown-700 text-sm font-medium cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform"
            >
              取消
            </button>
            <button
              onClick={handleDelete}
              className="flex-1 py-3 rounded-full bg-red-500 text-white text-sm font-semibold cursor-pointer whitespace-nowrap active:scale-[0.98] transition-transform"
            >
              删除
            </button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}